import { CapacityType } from '@/types';

export type FlowDashboardEntryDirection =
  | 'RECEIPT'
  | 'DELIVERY'
  | 'FORWARDHAUL'
  | 'BACKHAUL';

export type FlowDashboardLabelMode = 'label' | 'short_label';

export type FlowDashboardDisplaySettings = {
  labelMode: FlowDashboardLabelMode;
  showTspName: boolean;
};

type FlowDashboardLegacyColorMode =
  | 'utilization_gradient'
  | 'none'
  | 'low_flow_alert';

export type FlowDashboardFormattingSettings = {
  useCompactVolumes: boolean;
  compactVolumeCutoff: number;
  showUtilizationGradient: boolean;
  showLowFlowAlert: boolean;
  lowFlowThreshold: number;
  lowFlowAlertColor: string;
};

export type FlowDashboardFormattingOverrides =
  Partial<FlowDashboardFormattingSettings>;

export const FLOW_DASHBOARD_FORMATTING_KEY = 'flow_dashboard_formatting';

export type FlowDashboardHistoryScope = 'focused' | 'history';
export type FlowDashboardCycleScope = 'all_available' | 'pinned';
export type FlowDashboardCycleColumnMode =
  | 'all_cycles'
  | 'any_record'
  | 'flow_record';

export type FlowDashboardHistoryViewSettings = {
  defaultScope: FlowDashboardHistoryScope;
  focusedCycles: FlowDashboardCycleScope;
  pinnedCycle: 'T' | 'E' | 'I1' | 'I2' | 'I3';
  cycleColumnMode: FlowDashboardCycleColumnMode;
};

export type FlowDashboardHistoryViewOverrides =
  Partial<FlowDashboardHistoryViewSettings>;

export const FLOW_DASHBOARD_HISTORY_VIEW_KEY = 'flow_dashboard_history_view';

export const DEFAULT_FLOW_DASHBOARD_HISTORY_VIEW: FlowDashboardHistoryViewSettings = {
  defaultScope: 'focused',
  focusedCycles: 'all_available',
  pinnedCycle: 'I1',
  cycleColumnMode: 'flow_record',
};

export const DEFAULT_FLOW_DASHBOARD_FORMATTING: FlowDashboardFormattingSettings = {
  useCompactVolumes: false,
  compactVolumeCutoff: 15000,
  showUtilizationGradient: true,
  showLowFlowAlert: false,
  lowFlowThreshold: 0,
  lowFlowAlertColor: '#ff6b6b',
};

const LEGACY_COLOR_MODES: FlowDashboardLegacyColorMode[] = [
  'utilization_gradient',
  'none',
  'low_flow_alert',
];

const NOMINATION_CYCLES = ['T', 'E', 'I1', 'I2', 'I3'] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isValidColor = (value: unknown): value is string =>
  typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value);

const getFormattingOverrides = (
  config: Record<string, unknown> | null | undefined,
): FlowDashboardFormattingOverrides => {
  const formatting = config?.[FLOW_DASHBOARD_FORMATTING_KEY];
  if (!isRecord(formatting)) {
    return {};
  }

  const overrides: FlowDashboardFormattingOverrides = {};
  if (typeof formatting.use_compact_volumes === 'boolean') {
    overrides.useCompactVolumes = formatting.use_compact_volumes;
  }
  if (
    typeof formatting.compact_volume_cutoff === 'number' &&
    Number.isFinite(formatting.compact_volume_cutoff) &&
    formatting.compact_volume_cutoff >= 1000
  ) {
    overrides.compactVolumeCutoff = formatting.compact_volume_cutoff;
  }
  const utilizationGradientSetting = formatting.show_utilization_gradient;
  const lowFlowAlertSetting = formatting.show_low_flow_alert;
  if (typeof utilizationGradientSetting === 'boolean') {
    overrides.showUtilizationGradient = utilizationGradientSetting;
  }
  if (typeof lowFlowAlertSetting === 'boolean') {
    overrides.showLowFlowAlert = lowFlowAlertSetting;
  }
  if (
    typeof utilizationGradientSetting !== 'boolean' &&
    typeof lowFlowAlertSetting !== 'boolean' &&
    typeof formatting.color_mode === 'string' &&
    LEGACY_COLOR_MODES.includes(
      formatting.color_mode as FlowDashboardLegacyColorMode,
    )
  ) {
    overrides.showUtilizationGradient =
      formatting.color_mode === 'utilization_gradient';
    overrides.showLowFlowAlert = formatting.color_mode === 'low_flow_alert';
  }
  if (
    typeof formatting.low_flow_threshold === 'number' &&
    Number.isFinite(formatting.low_flow_threshold) &&
    formatting.low_flow_threshold >= 0
  ) {
    overrides.lowFlowThreshold = formatting.low_flow_threshold;
  }
  if (isValidColor(formatting.low_flow_alert_color)) {
    overrides.lowFlowAlertColor = formatting.low_flow_alert_color;
  }

  return overrides;
};

export const getFlowDashboardFormattingSettings = (
  dashboardLayout: Record<string, unknown> | null | undefined,
  widgetConfig?: Record<string, unknown> | null,
): FlowDashboardFormattingSettings => ({
  ...DEFAULT_FLOW_DASHBOARD_FORMATTING,
  ...getFormattingOverrides(dashboardLayout),
  ...getFormattingOverrides(widgetConfig),
});

export const getFlowDashboardFormattingOverrides = (
  widgetConfig: Record<string, unknown> | null | undefined,
): FlowDashboardFormattingOverrides => getFormattingOverrides(widgetConfig);

export const toFlowDashboardFormattingConfig = (
  settings: FlowDashboardFormattingOverrides,
): Record<string, boolean | number | string> => {
  const config: Record<string, boolean | number | string> = {};

  if (typeof settings.useCompactVolumes === 'boolean') {
    config.use_compact_volumes = settings.useCompactVolumes;
  }
  if (typeof settings.compactVolumeCutoff === 'number') {
    config.compact_volume_cutoff = settings.compactVolumeCutoff;
  }
  if (typeof settings.showUtilizationGradient === 'boolean') {
    config.show_utilization_gradient = settings.showUtilizationGradient;
  }
  if (typeof settings.showLowFlowAlert === 'boolean') {
    config.show_low_flow_alert = settings.showLowFlowAlert;
  }
  if (typeof settings.lowFlowThreshold === 'number') {
    config.low_flow_threshold = settings.lowFlowThreshold;
  }
  if (settings.lowFlowAlertColor) {
    config.low_flow_alert_color = settings.lowFlowAlertColor;
  }

  return config;
};

const getHistoryViewOverrides = (
  config: Record<string, unknown> | null | undefined,
): FlowDashboardHistoryViewOverrides => {
  const historyView = config?.[FLOW_DASHBOARD_HISTORY_VIEW_KEY];
  if (!isRecord(historyView)) {
    return {};
  }

  const overrides: FlowDashboardHistoryViewOverrides = {};
  if (
    historyView.default_scope === 'focused' ||
    historyView.default_scope === 'history'
  ) {
    overrides.defaultScope = historyView.default_scope;
  }
  if (
    historyView.focused_cycles === 'all_available' ||
    historyView.focused_cycles === 'pinned'
  ) {
    overrides.focusedCycles = historyView.focused_cycles;
  }
  if (
    typeof historyView.pinned_cycle === 'string' &&
    NOMINATION_CYCLES.includes(
      historyView.pinned_cycle as FlowDashboardHistoryViewSettings['pinnedCycle'],
    )
  ) {
    overrides.pinnedCycle =
      historyView.pinned_cycle as FlowDashboardHistoryViewSettings['pinnedCycle'];
  }
  if (
    historyView.cycle_column_mode === 'all_cycles' ||
    historyView.cycle_column_mode === 'any_record' ||
    historyView.cycle_column_mode === 'flow_record'
  ) {
    overrides.cycleColumnMode = historyView.cycle_column_mode;
  }

  return overrides;
};

export const getFlowDashboardHistoryViewSettings = (
  dashboardLayout: Record<string, unknown> | null | undefined,
  widgetConfig?: Record<string, unknown> | null,
): FlowDashboardHistoryViewSettings => ({
  ...DEFAULT_FLOW_DASHBOARD_HISTORY_VIEW,
  ...getHistoryViewOverrides(dashboardLayout),
  ...getHistoryViewOverrides(widgetConfig),
});

export const getFlowDashboardHistoryViewOverrides = (
  widgetConfig: Record<string, unknown> | null | undefined,
): FlowDashboardHistoryViewOverrides => getHistoryViewOverrides(widgetConfig);

export const toFlowDashboardHistoryViewConfig = (
  settings: FlowDashboardHistoryViewOverrides,
): Record<string, string> => {
  const config: Record<string, string> = {};

  if (settings.defaultScope) {
    config.default_scope = settings.defaultScope;
  }
  if (settings.focusedCycles) {
    config.focused_cycles = settings.focusedCycles;
  }
  if (settings.pinnedCycle) {
    config.pinned_cycle = settings.pinnedCycle;
  }
  if (settings.cycleColumnMode) {
    config.cycle_column_mode = settings.cycleColumnMode;
  }

  return config;
};

export const formatFlowDashboardVolume = (
  value: number,
  settings: FlowDashboardFormattingSettings,
): string => {
  if (
    !settings.useCompactVolumes ||
    Math.abs(value) < settings.compactVolumeCutoff
  ) {
    return new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    }).format(value);
  }

  return `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 0,
  }).format(value / 1000)}k`;
};

export const getFlowDashboardDisplaySettings = (
  config: Record<string, unknown> | null | undefined,
): FlowDashboardDisplaySettings => ({
  labelMode: config?.label_mode === 'short_label' ? 'short_label' : 'label',
  showTspName:
    typeof config?.show_tsp_name === 'boolean' ? config.show_tsp_name : true,
});

export const getFlowDashboardConceptStorageKey = (widgetId: number): string =>
  `flow_dashboard_widget_${widgetId}_concept`;

const POINT_DIRECTIONS: FlowDashboardEntryDirection[] = ['RECEIPT', 'DELIVERY'];
const SEGMENT_DIRECTIONS: FlowDashboardEntryDirection[] = [
  'FORWARDHAUL',
  'BACKHAUL',
];

export const getDirectionOptions = (
  capacityType: CapacityType,
): FlowDashboardEntryDirection[] =>
  capacityType === 'POINT' ? POINT_DIRECTIONS : SEGMENT_DIRECTIONS;